// apps/web/src/components/ideas/IdeaEditDrawer.tsx
import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Idea } from "@/hooks/use-ideas";
import type { SocialPlatform } from "@content-os/config";
import { Button } from "@/components/ui/button";

const PLATFORMS: SocialPlatform[] = ["INSTAGRAM", "TIKTOK", "TWITTER", "LINKEDIN", "FACEBOOK", "YOUTUBE", "THREADS", "PINTEREST"];
const FORMATS = ["image", "video", "carousel", "text"] as const;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialIdea: Partial<Idea> | null;  // null = create mode
  onSubmit: (input: Partial<Idea>) => Promise<unknown>;
}

export function IdeaEditDrawer({ open, onOpenChange, initialIdea, onSubmit }: Props) {
  const { t } = useTranslation("ideas");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [platforms, setPlatforms] = useState<SocialPlatform[]>([]);
  const [formats, setFormats] = useState<string[]>([]);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(initialIdea?.title ?? "");
      setDescription(initialIdea?.description ?? "");
      setPlatforms((initialIdea?.platforms as SocialPlatform[]) ?? []);
      setFormats(initialIdea?.formats ?? []);
      setAiGenerated(initialIdea?.aiGenerated ?? false);
    }
  }, [open, initialIdea]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({ title: title.trim(), description: description.trim(), platforms, formats, aiGenerated });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed right-0 top-0 z-50 h-full w-full max-w-md bg-background p-6 shadow-lg overflow-y-auto">
          <Dialog.Title className="text-lg font-semibold">{initialIdea?.id ? t("ideas:drawer.titleEdit") : t("ideas:drawer.titleNew")}</Dialog.Title>
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t("ideas:drawer.fields.title")}</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="block w-full rounded border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("ideas:drawer.fields.description")}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                className="block w-full rounded border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("ideas:drawer.fields.platforms")}</label>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map((p) => (
                  <label key={p} className="flex items-center gap-1 text-xs rounded border px-2 py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={platforms.includes(p)}
                      onChange={() => setPlatforms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p])}
                    />
                    {p}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("ideas:drawer.fields.formats")}</label>
              <div className="flex flex-wrap gap-2">
                {FORMATS.map((f) => (
                  <label key={f} className="flex items-center gap-1 text-xs rounded border px-2 py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formats.includes(f)}
                      onChange={() => setFormats((prev) => prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f])}
                    />
                    {f}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={aiGenerated} onChange={(e) => setAiGenerated(e.target.checked)} />
              {t("ideas:drawer.fields.aiGenerated")}
            </label>
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild><Button type="button" variant="outline">{t("common:actions.cancel")}</Button></Dialog.Close>
              <Button type="submit" disabled={submitting || !title.trim()}>
                {submitting ? t("ideas:drawer.saving") : t("common:actions.save")}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
